import { useEffect, useMemo, useRef, useState } from 'react';
import { C, ink } from '../lib/theme';
import { IconMinus, IconPlus } from './icons';

const CROP_SIZE = 220;
const OUTPUT_SIZE = 480;

/**
 * Editor de posição/zoom da foto de perfil (pedido explícito: "dar para
 * posicionar a foto assim como dar zoom") — arrastar a imagem dentro do
 * círculo + slider de zoom, depois "Salvar" recorta um JPEG quadrado do
 * enquadramento escolhido (o círculo final vem do próprio `Avatar`, que já
 * aplica `border-radius: 50%` na renderização — não precisa cravar o
 * recorte circular no arquivo).
 */
export function AvatarCropper({ file, onCancel, onConfirm, busy }: {
  file: File;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
  busy: boolean;
}) {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  const imgRef = useRef<HTMLImageElement>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  const baseScale = natural ? Math.max(CROP_SIZE / natural.w, CROP_SIZE / natural.h) : 1;
  const scale = baseScale * zoom;
  const drawW = (natural?.w ?? 0) * scale;
  const drawH = (natural?.h ?? 0) * scale;
  const maxX = Math.max(0, (drawW - CROP_SIZE) / 2);
  const maxY = Math.max(0, (drawH - CROP_SIZE) / 2);

  function clamp(p: { x: number; y: number }) {
    return { x: Math.min(maxX, Math.max(-maxX, p.x)), y: Math.min(maxY, Math.max(-maxY, p.y)) };
  }

  function onZoomChange(v: number) {
    setZoom(v);
    setPos((p) => clamp(p));
  }

  function onPointerDown(e: React.PointerEvent) {
    (e.target as Element).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPos(clamp({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy }));
  }
  function onPointerUp() { dragRef.current = null; }

  function confirm() {
    if (!natural) return;
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const k = OUTPUT_SIZE / CROP_SIZE;
    const dw = drawW * k, dh = drawH * k;
    const dx = CROP_SIZE * k / 2 - dw / 2 + pos.x * k;
    const dy = CROP_SIZE * k / 2 - dh / 2 + pos.y * k;
    ctx.drawImage(imgRef.current!, 0, 0, natural.w, natural.h, dx, dy, dw, dh);
    canvas.toBlob((blob) => { if (blob) onConfirm(blob); }, 'image/jpeg', 0.92);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '6px 0 4px' }}>
      <div
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
        style={{
          width: CROP_SIZE, height: CROP_SIZE, borderRadius: '50%', overflow: 'hidden', position: 'relative',
          cursor: dragRef.current ? 'grabbing' : 'grab', touchAction: 'none',
          border: `2px solid ${ink(0.16)}`, boxShadow: `0 0 0 5px ${C.accent}22, 0 12px 30px -10px rgba(8,8,16,0.6)`,
          background: ink(0.04),
        }}
      >
        <img
          ref={imgRef} src={url} alt="" draggable={false}
          onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
          style={{
            position: 'absolute', top: '50%', left: '50%', width: drawW || 'auto', height: drawH || 'auto',
            transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px))`,
            userSelect: 'none', pointerEvents: 'none', maxWidth: 'none',
          }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', maxWidth: 260 }}>
        <span style={{ color: ink(0.45), flex: 'none' }}><IconMinus size={13} /></span>
        <input
          type="range" min={1} max={3} step={0.01} value={zoom}
          onChange={(e) => onZoomChange(Number(e.target.value))}
          style={{ flex: 1, accentColor: C.accent }}
        />
        <span style={{ color: ink(0.45), flex: 'none' }}><IconPlus size={13} /></span>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <button
          type="button" disabled={busy || !natural} onClick={confirm}
          style={{
            border: 'none', cursor: busy ? 'wait' : 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 700,
            padding: '9px 18px', borderRadius: 11, color: '#fff', opacity: busy || !natural ? 0.6 : 1,
            background: `linear-gradient(150deg, ${C.accent}, ${C.accentDeep})`,
            boxShadow: '0 6px 18px -2px rgba(232,85,48,0.5)',
          }}
        >Salvar foto</button>
        <button
          type="button" disabled={busy} onClick={onCancel}
          style={{
            border: `1px solid ${ink(0.15)}`, background: ink(0.05), color: ink(0.7),
            cursor: busy ? 'wait' : 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 650,
            padding: '9px 14px', borderRadius: 11, opacity: busy ? 0.6 : 1,
          }}
        >Cancelar</button>
      </div>
    </div>
  );
}
